import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Alert from '@mui/material/Alert'
import { PageLayout, PageHeader } from '../../components'
import { ResponsiveTable, TableEmptyState } from '../../components/TableComponents'
import { StatusChip } from '../../components/atoms'
import { useFetch } from '../../hooks'
import useAuthz from '../../hooks/useAuthz'
import styles from './Availability.module.scss'
import { apiEndpoints } from '../../consts'

const AvailabilityPage = ({ isAuthenticated, campaign }) => {
  const { isAdmin } = useAuthz()
  const [sortField, setSortField] = useState('isAvailable')
  const [sortDirection, setSortDirection] = useState('asc')

  const { data: users, loading, error } = useFetch(
    apiEndpoints.userAvailabilityUsersOrg(campaign),
    {
      initialData: [],
      skip: !isAuthenticated || !isAdmin || !campaign,
      dependencies: [campaign],
      transform: (data) => data.users || [],
      errorMessage: 'Failed to fetch user availability data'
    }
  )

  const handleSort = (field) => {
    const isAsc = sortField === field && sortDirection === 'asc'
    setSortDirection(isAsc ? 'desc' : 'asc')
    setSortField(field)
  }

  const columns = useMemo(() => [
    {
      id: 'campaign_name',
      label: 'Campaign',
      sortable: false,
      style: { width: '18%' },
      renderCell: () => campaign
    },
    {
      id: 'user_name',
      label: 'User Name',
      sortable: false,
      style: { width: '10%' },
      renderCell: (row) => row.user_name || 'Unknown'
    },
    {
      id: 'isAvailable',
      label: 'Availability Status',
      sortable: true,
      align: 'center',
      style: { width: '15%' },
      sortComparator: (a, b) => b.isAvailable - a.isAvailable,
      renderSortIcon: (direction) => (
        <span style={{ marginLeft: '4px', fontSize: 'var(--font-size-sm)' }}>
          {direction === 'asc' ? '↑' : '↓'}
        </span>
      ),
      renderCell: (row) => (
        <StatusChip
          label={row.isAvailable ? 'Available' : 'Unavailable'}
          color={row.isAvailable ? 'success' : 'error'}
          size='small'
        />
      )
    }
  ], [campaign])

  const sortedUsers = useMemo(() => {
    if (!sortField) return users

    const column = columns.find(col => col.id === sortField)
    if (!column || !column.sortComparator) return users

    return [...users].sort((a, b) => {
      const result = column.sortComparator(a, b)
      return sortDirection === 'asc' ? result : -result
    })
  }, [users, sortField, sortDirection, columns])

  return (
    <PageLayout>
      <PageHeader
        title='User Availability'
        subtitle='View availability status for all users assigned to your current campaign'
      />

      {error && (
        <Alert
          severity='error'
          className={styles.errorAlert}
          onClose={() => {}}
        >
          {error}
        </Alert>
      )}

      <ResponsiveTable
        columns={columns}
        data={sortedUsers}
        loading={loading}
        searched={false}
        variant='availability'
        colSpan={columns.length}
        emptyState={
          <TableEmptyState
            searched={false}
            title='No Users Found'
            message='No users are currently assigned to this campaign'
            icon='users'
          />
        }
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        containerProps={{
          className: styles.tableContainer
        }}
        tableProps={{
          'aria-label': 'user availability table'
        }}
      />
    </PageLayout>
  )
}

AvailabilityPage.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  campaign: PropTypes.string
}

export default AvailabilityPage
